import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTrial } from './contexts/TrialContext';
import { useLobbySocket } from './contexts/LobbySocketContext';
import { useMultiplayerSocket } from './hooks/useMultiplayerSocket';
import { useTranslation } from './contexts/LanguageContext';
import { useNotifications } from './hooks/useNotifications';
import { useGlobalNotifications } from './hooks/useGlobalNotifications';
import { useLobbyMessages } from './hooks/useLobbyMessages';
import { useSettingsSync } from './hooks/useSettingsSync';
import CoverScreen from './components/CoverScreen';
import AboutUs from './components/AboutUs';
import CourtyardLobby from './components/CourtyardLobby';
import ProfilePage from './components/ProfilePage';
import TournamentLobby from './components/TournamentLobby';
import TournamentBracket from './components/TournamentBracket';
import RankedMatchmaking from './components/RankedMatchmaking';
import ProModeOnboarding from './components/ProModeOnboarding';
import VersionMonitor from './components/VersionMonitor';
import DiagnosticsPage from './components/DiagnosticsPage';
import InstallPWAButton from './components/InstallPWAButton';
import OutdatedVersionBanner from './components/OutdatedVersionBanner';
import SubscriptionPage from './components/SubscriptionPage';

// Haptic feedback utility for mobile devices
const triggerHapticFeedback = (type = 'light') => {
  if ('vibrate' in navigator) {
    switch (type) {
      case 'light':
        navigator.vibrate(10);
        break;
      case 'medium':
        navigator.vibrate(25);
        break;
      case 'heavy':
        navigator.vibrate(50);
        break;
      case 'notification':
        navigator.vibrate([50, 50, 50]); // Three short pulses
        break;
      case 'turn':
        navigator.vibrate([100, 50, 100]); // Two pulses for your turn
        break;
      default:
        navigator.vibrate(10);
    }
    return true;
  }
  return false;
};
import RulesScreen from './components/RulesScreen';
import ChessBoard from './components/ChessBoard';
import ChessBoard3D from './components/ChessBoard3D';
import GameStatus from './components/GameStatus';
import PromotionPopup from './components/PromotionPopup';
import EmbodimentChoiceModal from './components/EmbodimentChoiceModal';
import NewGamePanel from './components/NewGamePanel';
import SettingsModal from './components/SettingsModal';
import AuthModal from './components/AuthModal';
import MultiplayerLobby from './components/MultiplayerLobby';
import WaitingRoom from './components/WaitingRoom';
import GuestPrompt from './components/GuestPrompt';
import TrialBanner from './components/TrialBanner';
import StoreModal from './components/StoreModal';
import GameOverModal from './components/GameOverModal';
import MoveHistory from './components/MoveHistory';
import MoveTimer from './components/MoveTimer';
import ChatBox from './components/ChatBox';
import SpectatorChat from './components/SpectatorChat';
import LobbyChat from './components/LobbyChat';
import Assistant from './components/Assistant';
import Troubleshooter from './components/Troubleshooter';
import UserProfile from './components/UserProfile';
import NotificationBell from './components/NotificationBell';
import FriendsSystem from './components/FriendsSystem';
import ThemeToggle from './components/ThemeToggle';
import AccountManager from './components/AccountManager';
import PlayerInfoPanel from './components/PlayerInfoPanel';
import { AvatarSelector } from './components/UserProfile';
import './App.css';
import './styles/ExpertTheme.css';
import './styles/ThemeToggle.css';
import logger from './utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const { user, isAuthenticated, isGuest, setUser, setToken, loading, token: authToken, processGoogleCallback } = useAuth();
  const { isTrialActive, hasActiveAccess } = useTrial();
  const { connected: lobbyConnected, addMessageHandler } = useLobbySocket();
  const [screen, setScreen] = useState('cover'); // 'cover', 'rules', 'game'
  const [gameId, setGameId] = useState(null);
  const [gameMode, setGameMode] = useState('solo'); // 'solo' or 'multiplayer'
  const [gameState, setGameState] = useState(null);
  
  // Push notifications hook
  const { 
    notifyYourTurn, 
    notifyMatchFound, 
    notifySpectatorRequest, 
    notifyRejoinRequest,
    notifyFriendRequest,
    notifyMessage,
    playSound 
  } = useNotifications();

  // Google OAuth callback handler - detect session_id in URL hash
  const googleCallbackProcessed = useRef(false);
  useEffect(() => {
    const hash = window.location.hash;
    if (hash?.includes('session_id=') && !googleCallbackProcessed.current) {
      googleCallbackProcessed.current = true;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        processGoogleCallback(sessionId).then((result) => {
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          if (result.success) {
            setScreen('lobby');
          }
        });
      }
    }
  }, [processGoogleCallback]);

  // PWA convenience: when the app is launched in installed/standalone mode
  // (or explicitly via ?source=pwa… shortcut) and the user is already
  // authenticated, skip the ENTER cover screen and land directly on the
  // Courtyard — matches native-app expectations where signing in once
  // persists across launches.
  const pwaAutoAdvanceDone = useRef(false);
  useEffect(() => {
    if (pwaAutoAdvanceDone.current) return;
    if (loading) return;
    if (!isAuthenticated) return;
    if (screen !== 'cover') return;
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    const urlParams = new URLSearchParams(window.location.search);
    const launchedFromPwa = urlParams.get('source') === 'pwa' || urlParams.get('source')?.startsWith('pwa');
    if (isStandalone || launchedFromPwa) {
      pwaAutoAdvanceDone.current = true;
      setScreen('lobby');
    }
  }, [loading, isAuthenticated, screen]);

  // Variant display configuration with icons and names
  const getVariantDisplay = (variantId) => {
    const variants = {
      'standard': { icon: '♟️', name: 'Standard Chess' },
      'kings_court_2dukes': { icon: '👑', name: 'Chess Kingdom (Two Dukes)' },
      'players_choice': { icon: '🎯', name: "Player's Choice" },
      'instant_king': { icon: '⚡', name: 'Instant King' },
      'fight_to_be_king': { icon: '⚔️', name: 'Fight to be King' },
      'pawns_wild': { icon: '🛡️', name: "King's Court: Pawn's Wild" },
      'handicap_mode': { icon: '⚖️', name: 'Standard Chess vs Royal Chess Kingdom' }
    };

    const variant = variants[variantId] || variants['kings_court_2dukes'];
    
    return (
      <div className="variant-title">
        <span className="variant-icon">{variant.icon}</span>
        <span className="variant-name">{variant.name}</span>
      </div>
    );
  };
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  // Cache of ALL legal moves for the current side-to-move, refreshed once per
  // gameState change. Used to filter by "from" square locally so clicks don't
  // incur a network roundtrip for highlighting.
  const [allLegalMoves, setAllLegalMoves] = useState([]);
  const allLegalMovesGameIdRef = useRef(null);
  const [inCheck, setInCheck] = useState(false);
  const [isCheckmate, setIsCheckmate] = useState(false);
  const [isStalemate, setIsStalemate] = useState(false);
  const [winner, setWinner] = useState(null);
  const [showNewGamePanel, setShowNewGamePanel] = useState(false);
  const [showPromotionPopup, setShowPromotionPopup] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [showEmbodimentModal, setShowEmbodimentModal] = useState(false);
  const [pendingEmbodimentSide, setPendingEmbodimentSide] = useState(null);
  const [opponentInfo, setOpponentInfo] = useState(null); // Store opponent's info
  const [whitePlayerInfo, setWhitePlayerInfo] = useState(null); // Store white player info (for spectators)
  const [blackPlayerInfo, setBlackPlayerInfo] = useState(null); // Store black player info (for spectators)
  const [vsAI, setVsAI] = useState(false); // Track if playing against AI
  const [availableDukes, setAvailableDukes] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMultiplayerLobby, setShowMultiplayerLobby] = useState(false);
  const [showTournamentLobby, setShowTournamentLobby] = useState(false);
  const [showTournamentBracket, setShowTournamentBracket] = useState(false);
  const [activeTournamentId, setActiveTournamentId] = useState(null);
  const [showRankedMatchmaking, setShowRankedMatchmaking] = useState(false);  // Ranked system
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [waitingRoomInfo, setWaitingRoomInfo] = useState(null);
  const [playerColor, setPlayerColor] = useState(null); // 'white' or 'black'
  const [isSpectator, setIsSpectator] = useState(false); // Spectator mode flag
  const [moveTimerLimit, setMoveTimerLimit] = useState(null); // seconds per move (null = no limit)
  
  // Do Not Disturb mode state (for room owners during active games)
  const [doNotDisturb, setDoNotDisturb] = useState(false);
  
  // Board flip state for viewing from opposite side
  const [boardFlipped, setBoardFlipped] = useState(false);
  
  // Global notifications (extracted to hook)
  const {
    globalNotifications,
    addGlobalNotification,
    clearGlobalNotification,
    clearAllGlobalNotifications
  } = useGlobalNotifications();
  
  // Add notification globally - alias for backward compat
  
  // Ref to hold handleGameStart function for use in callbacks
  const handleGameStartRef = useRef(null);
  
  // Handle entering a room from notification (available in all views)
  const handleEnterRoomFromNotification = useCallback(async (gameId, roomName) => {
    setShowMultiplayerLobby(false);
    if (handleGameStartRef.current) {
      handleGameStartRef.current(gameId, 'multiplayer', roomName);
    }
  }, []);

  // Lobby WebSocket message handling (extracted to hook)
  useLobbyMessages({
    lobbyConnected,
    addMessageHandler,
    addGlobalNotification,
    notifyMatchFound,
    notifyFriendRequest,
    notifyMessage,
    notifySpectatorRequest,
    notifyRejoinRequest,
    playSound,
    handleGameStartRef,
    setShowMultiplayerLobby
  });

  // Listen for trial expiration notifications from TrialContext
  useEffect(() => {
    const handleTrialNotification = (e) => {
      const { type, message } = e.detail;
      addGlobalNotification(type, null, 'Trial', message);
    };
    window.addEventListener('trialNotification', handleTrialNotification);
    return () => window.removeEventListener('trialNotification', handleTrialNotification);
  }, [addGlobalNotification]);

  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [showLobbyChat, setShowLobbyChat] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'solo' or 'multiplayer'
  const [moveHistory, setMoveHistory] = useState([]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  
  // Settings sync hook (view mode, skins, CSS variables)
  const {
    viewMode, setViewMode,
    theme3D, setTheme3D,
    pieceSkinWhite, setPieceSkinWhite,
    pieceSkinBlack, setPieceSkinBlack,
    dukeSkinWhite, setDukeSkinWhite,
    dukeSkinBlack, setDukeSkinBlack
  } = useSettingsSync({ gameMode, screen });

  const [showAssistant, setShowAssistant] = useState(false);
  const [showTroubleshooter, setShowTroubleshooter] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [gameOverData, setGameOverData] = useState({ winner: null, reason: null });
  const [showFriends, setShowFriends] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [showAccountManager, setShowAccountManager] = useState(false);
  
  // Rematch state for multiplayer games
  const [rematchState, setRematchState] = useState(null); // null, 'waiting', 'opponent_requested', 'accepted', 'declined'
  
  // Host theme indicator for multiplayer (shows when playing with someone else's theme)
  const [hostThemeInfo, setHostThemeInfo] = useState(null); // { hostName: string, isUsingHostTheme: boolean }

  // Reset move history when starting a new game
  useEffect(() => {
    if (gameId) {
      setMoveHistory([]);
      setCurrentMoveIndex(0);
      setRematchState(null); // Reset rematch state for new game
      setHostThemeInfo(null); // Reset host theme indicator
    }
  }, [gameId]);

  // Determine player color, opponent info, and apply host settings when game loads
  useEffect(() => {
    if (gameState && (gameMode === 'multiplayer' || gameMode === 'spectate') && user) {
      // Fetch room info to determine color, opponent, and host settings
      const fetchPlayerColorAndOpponent = async () => {
        try {
          const response = await axios.get(`${API}/multiplayer/rooms/${gameId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const roomData = response.data;
          
          let opponentId = null;
          
          if (roomData.white_player_id === user.user_id) {
            setPlayerColor('white');
            opponentId = roomData.black_player_id;
          } else if (roomData.black_player_id === user.user_id) {
            setPlayerColor('black');
            opponentId = roomData.white_player_id;  // Opponent is WHITE player
          }
          
          // Check if user is the host (host doesn't need to see the indicator)
          const isHost = roomData.host_user_id === user.user_id;
          const hostName = roomData.host_name || 'Host';
          
          // Apply host settings for multiplayer (both players and spectators see host's visuals)
          // First check if we have host settings from join response (stored in window)
          let hostSettings = roomData.host_settings;
          if (!hostSettings && window.chessKingdomHostSettings) {
            hostSettings = window.chessKingdomHostSettings;
            // Clear after use
            delete window.chessKingdomHostSettings;
          }
          
          if (hostSettings) {
            // Set the host theme indicator (only for non-host players)
            if (!isHost && (hostSettings.piece_skin_white || hostSettings.piece_skin_black || 
                hostSettings.board_theme !== 'default')) {
              setHostThemeInfo({
                hostName: hostName,
                isUsingHostTheme: true
              });
            }
            
            // Apply piece skins
            if (hostSettings.piece_skin_white !== undefined) {
              setPieceSkinWhite(hostSettings.piece_skin_white);
              if (typeof window !== 'undefined') {
                window.chessKingdomPieceSkinWhite = hostSettings.piece_skin_white;
              }
            }
            if (hostSettings.piece_skin_black !== undefined) {
              setPieceSkinBlack(hostSettings.piece_skin_black);
              if (typeof window !== 'undefined') {
                window.chessKingdomPieceSkinBlack = hostSettings.piece_skin_black;
              }
            }
            
            // Apply duke skins
            if (hostSettings.duke_skin_white) {
              setDukeSkinWhite(hostSettings.duke_skin_white);
              if (typeof window !== 'undefined') {
                window.chessKingdomDukeSkinWhite = hostSettings.duke_skin_white;
              }
            }
            if (hostSettings.duke_skin_black) {
              setDukeSkinBlack(hostSettings.duke_skin_black);
              if (typeof window !== 'undefined') {
                window.chessKingdomDukeSkinBlack = hostSettings.duke_skin_black;
              }
            }
            
            // Apply board theme via CSS variables (same method as SettingsModal)
            if (hostSettings.board_theme && hostSettings.board_theme !== 'default') {
              const BOARD_THEMES = [
                { id: 'classic', light: '#f0d9b5', dark: '#b58863' },
                { id: 'blue', light: '#dee3e6', dark: '#8ca2ad' },
                { id: 'green', light: '#ffffdd', dark: '#86a666' },
                { id: 'purple', light: '#e8d4f2', dark: '#9b59b6' },
                { id: 'brown', light: '#f4e4c1', dark: '#a67c52' },
                { id: 'gray', light: '#e6e6e6', dark: '#888888' },
                { id: 'gold', light: '#f5e6c8', dark: '#c9a227' },
                { id: 'obsidian', light: '#c0c0c0', dark: '#303030' },
                { id: 'pink', light: '#ffe4ec', dark: '#d4849a' },
              ];
              const theme = BOARD_THEMES.find(t => t.id === hostSettings.board_theme);
              if (theme) {
                const root = document.documentElement;
                root.style.setProperty('--board-light', theme.light);
                root.style.setProperty('--board-dark', theme.dark);
              }
            }
            
            // Apply piece colors via CSS variables
            if (hostSettings.use_custom_pieces && hostSettings.piece_color_white && hostSettings.piece_color_black) {
              const root = document.documentElement;
              root.style.setProperty('--piece-white', hostSettings.piece_color_white);
              root.style.setProperty('--piece-black', hostSettings.piece_color_black);
            } else if (hostSettings.piece_theme && hostSettings.piece_theme !== 'classic') {
              // Apply piece theme using official theme definitions
              const PIECE_THEMES = [
                { id: 'classic', white: '#ffffff', black: '#2d2d2d' },
                { id: 'red_blue', white: '#e74c3c', black: '#3498db' },
                { id: 'gold_silver', white: '#f1c40f', black: '#95a5a6' },
                { id: 'green_purple', white: '#2ecc71', black: '#9b59b6' },
                { id: 'orange_teal', white: '#e67e22', black: '#1abc9c' },
              ];
              const pieceTheme = PIECE_THEMES.find(t => t.id === hostSettings.piece_theme);
              if (pieceTheme) {
                const root = document.documentElement;
                root.style.setProperty('--piece-white', pieceTheme.white);
                root.style.setProperty('--piece-black', pieceTheme.black);
                root.setAttribute('data-piece-theme', hostSettings.piece_theme);
              } else {
                logger.warn(`🎨 Unknown piece theme: ${hostSettings.piece_theme}`);
              }
            }
            
            // Apply grid settings from host (creator's grid settings apply to all)
            if (hostSettings.grid_settings) {
              const gs = hostSettings.grid_settings;
              const root = document.documentElement;
              const thicknessMap = { thin: '1px', medium: '2px', thick: '3px' };
              
              root.style.setProperty('--grid-enabled', gs.enabled ? '1' : '0');
              root.style.setProperty('--grid-inner', gs.innerGrid ? '1' : '0');
              root.style.setProperty('--grid-outer', gs.outerBorder ? '1' : '0');
              root.style.setProperty('--grid-color', gs.color || '#000000');
              root.style.setProperty('--grid-thickness', thicknessMap[gs.thickness] || '1px');
              root.style.setProperty('--grid-style', gs.style || 'solid');
              
              // Store in window for ChessBoard to pick up
              window.chessKingdomGridSettings = gs;
              
              // Dispatch event to notify ChessBoard component
              window.dispatchEvent(new CustomEvent('settingsChanged', {
                detail: { gridSettings: gs, isMultiplayer: true }
              }));
            }
            // Clean up host settings from localStorage to prevent re-application issues
            try {
              const currentSettings = JSON.parse(localStorage.getItem('chessKingdomSettings') || '{}');
              delete currentSettings.hostPieceSkinWhite;
              delete currentSettings.hostPieceSkinBlack;
              delete currentSettings.hostDukeSkinWhite;
              delete currentSettings.hostDukeSkinBlack;
              delete currentSettings.hostBoardTheme;
              delete currentSettings.hostPieceTheme;
              delete currentSettings.hostPieceColorWhite;
              delete currentSettings.hostPieceColorBlack;
              delete currentSettings.hostUseCustomPieces;
              delete currentSettings.hostCoordinateColor;
              localStorage.setItem('chessKingdomSettings', JSON.stringify(currentSettings));
            } catch (e) {
              logger.warn('Failed to cleanup host settings:', e);
            }
          }
          
          // Set move timer limit if room has one
          if (roomData.move_timer) {
            setMoveTimerLimit(roomData.move_timer);
          } else {
            setMoveTimerLimit(null);
          }
          
          // Fetch both players' info for spectator mode
          if (isSpectator || gameMode === 'spectate') {
            // Fetch white player info
            if (roomData.white_player_id) {
              try {
                const whiteResponse = await axios.get(`${API}/users/${roomData.white_player_id}/profile`, {
                  headers: { 'Authorization': `Bearer ${authToken}` }
                });
                setWhitePlayerInfo({
                  user_id: roomData.white_player_id,
                  name: whiteResponse.data.name || whiteResponse.data.username || 'White Player',
                  avatar: whiteResponse.data.avatar || '👤'
                });
              } catch (err) {
                setWhitePlayerInfo({ user_id: roomData.white_player_id, name: 'White Player', avatar: '👤' });
              }
            }
            // Fetch black player info
            if (roomData.black_player_id) {
              try {
                const blackResponse = await axios.get(`${API}/users/${roomData.black_player_id}/profile`, {
                  headers: { 'Authorization': `Bearer ${authToken}` }
                });
                setBlackPlayerInfo({
                  user_id: roomData.black_player_id,
                  name: blackResponse.data.name || blackResponse.data.username || 'Black Player',
                  avatar: blackResponse.data.avatar || '👤'
                });
              } catch (err) {
                setBlackPlayerInfo({ user_id: roomData.black_player_id, name: 'Black Player', avatar: '👤' });
              }
            }
          }
          
          // Fetch opponent's profile (for regular players)
          if (opponentId) {
            try {
              const opponentResponse = await axios.get(`${API}/users/${opponentId}/profile`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
              });
              setOpponentInfo({
                user_id: opponentId,
                name: opponentResponse.data.name || opponentResponse.data.username || 'Opponent',
                avatar: opponentResponse.data.avatar || '👤',
                isPaid: opponentResponse.data.isPaid || false,
                rank: opponentResponse.data.rank || null,
                wins: opponentResponse.data.wins || 0,
                gamesPlayed: opponentResponse.data.gamesPlayed || 0
              });
            } catch (err) {
              setOpponentInfo({
                user_id: opponentId,
                name: 'Opponent',
                avatar: '👤',
                isPaid: false,
                rank: null
              });
            }
          }
        } catch (error) {
          logger.error('Failed to determine player color:', error);
        }
      };
      
      fetchPlayerColorAndOpponent();
    }
  }, [gameState, gameMode, gameId, user, authToken, API, setPieceSkinWhite, setPieceSkinBlack, setDukeSkinWhite, setDukeSkinBlack]);
  
  // Ref to hold the latest fetchGameState for use in callbacks
  const fetchGameStateRef = useRef(null);
  
  // Fetch game state from server
  const fetchGameState = async () => {
    if (!gameId) return;
    
    try {
      const response = await axios.get(`${API}/chess/game/${gameId}`);
      const { board_state, legal_moves, in_check, is_checkmate, is_stalemate } = response.data;
      
      setGameState(board_state);
      setLegalMoves(legal_moves || []);
      setInCheck(in_check);
      setIsCheckmate(is_checkmate);
      setIsStalemate(is_stalemate);
    } catch (error) {
      logger.error('[FETCH STATE] Error fetching game state:', error);
    }
  };
  
  // Keep ref updated
  useEffect(() => {
    fetchGameStateRef.current = fetchGameState;
  });
  
  const handleMoveReceived = (move, boardState) => {
    // IMPORTANT: Always clear legal moves when turn changes
    // This prevents stale highlights from appearing after turn skip
    setLegalMoves([]);
    setSelectedSquare(null);
    
    // If we received board state directly, use it
    if (boardState) {
      setGameState(boardState);
      
      // Check game status from the board state
      if (boardState.in_check !== undefined) {
        setInCheck(boardState.in_check);
      }
      if (boardState.is_checkmate !== undefined) {
        setIsCheckmate(boardState.is_checkmate);
      }
      if (boardState.is_stalemate !== undefined) {
        setIsStalemate(boardState.is_stalemate);
      }
    } else {
      // Fall back to fetching from server
      fetchGameState();
    }
  };

  const handleThemeChanged = (theme) => {
    setTheme3D(theme);
  };

  const handleGameOverSocket = (winnerSide, reason = 'checkmate', message = null) => {
    // Set the game over data and show the modal
    setGameOverData({
      winner: winnerSide,
      reason: reason,
      message: message
    });
    setShowGameOver(true);
    setWinner(winnerSide);
    // Also set checkmate state for UI consistency
    if (reason === 'checkmate') {
      setIsCheckmate(true);
    }
  };

  const handlePlayerDisconnected = (userId) => {
    // Only show disconnect alert if game is still active (not already showing game over)
    if (!showGameOver && gameState) {
      alert('Opponent disconnected! They may reconnect shortly.');
    }
  };

  // Reset game state function - shared by multiple handlers
  const closeGameAndReturnToLobby = useCallback(() => {
    setShowGameOver(false);
    setGameId(null);
    setGameState(null);
    setGameOverData({ winner: null, reason: null, message: '' });
    setIsCheckmate(false);
    setIsStalemate(false);
    setSelectedSquare(null);
    setLegalMoves([]);
    setMoveHistory([]);
    setCurrentMoveIndex(0);
    setPlayerColor(null);
    setIsSpectator(false);
    setGameMode('solo');
    setScreen('lobby');
    setRematchState(null);
  }, []);

  // Handle rematch updates from WebSocket
  const handleRematchUpdate = useCallback((state, newBoardState) => {
    setRematchState(state);
    
    if (state === 'accepted' && newBoardState) {
      // Reset the game with new board state
      setTimeout(() => {
        setGameState(newBoardState);
        setShowGameOver(false);
        setRematchState(null);
        setMoveHistory([]);
        setCurrentMoveIndex(0);
      }, 1500); // Short delay to show "Starting new game..."
    } else if (state === 'declined') {
      // Return to lobby after a short delay
      setTimeout(() => {
        closeGameAndReturnToLobby();
      }, 2000);
    }
  }, [closeGameAndReturnToLobby]);

  // Handle in-game spectator request notification
  const handleInGameSpectatorRequest = useCallback((data) => {
    addGlobalNotification(
      'spectator_request',
      gameId,
      data.user_name || 'Someone',
      `${data.user_name || 'Someone'} wants to spectate this game`,
      data.user_id // Store requester ID
    );
    // Push notification with sound
    notifySpectatorRequest(data.user_name || 'Someone');
  }, [gameId, addGlobalNotification, notifySpectatorRequest]);

  // Handle in-game rejoin request notification
  const handleInGameRejoinRequest = useCallback((data) => {
    addGlobalNotification(
      'rejoin_request',
      data.game_id,
      data.room_name || 'Room',
      `${data.requester_name || 'Someone'} wants to rejoin`,
      data.requester_id // Store requester ID
    );
    // Push notification with sound
    notifyRejoinRequest(data.requester_name || 'Someone', data.room_name || 'Room');
  }, [addGlobalNotification, notifyRejoinRequest]);

  // Handler for approving spectator request from notification bell
  const handleApproveSpectator = useCallback(async (gameIdToApprove, requesterId) => {
    try {
      const response = await axios.post(
        `${API}/multiplayer/spectate/${gameIdToApprove}/approve/${requesterId}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      return response.data;
    } catch (error) {
      logger.error('❌ Failed to approve spectator:', error);
      alert('Failed to approve spectator request');
    }
  }, [authToken]);

  // Handler for denying spectator request from notification bell
  const handleDenySpectator = useCallback(async (gameIdToDeny, requesterId) => {
    try {
      const response = await axios.post(
        `${API}/multiplayer/spectate/${gameIdToDeny}/deny/${requesterId}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      return response.data;
    } catch (error) {
      logger.error('❌ Failed to deny spectator:', error);
      alert('Failed to deny spectator request');
    }
  }, [authToken]);

  // Handler for approving rejoin request from notification bell
  const handleApproveRejoin = useCallback(async (gameIdToApprove, requesterId) => {
    try {
      const response = await axios.post(
        `${API}/multiplayer/rooms/${gameIdToApprove}/approve-rejoin/${requesterId}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      // After approving, automatically navigate the host to the game
      setShowMultiplayerLobby(false);
      if (handleGameStartRef.current) {
        // Get room name from the response or use a default
        const roomName = response.data?.room_name || 'Game Room';
        handleGameStartRef.current(gameIdToApprove, 'multiplayer', roomName);
      }
      
      return response.data;
    } catch (error) {
      logger.error('❌ Failed to approve rejoin:', error);
      alert('Failed to approve rejoin request');
    }
  }, [authToken]);

  // Handler for denying rejoin request from notification bell
  const handleDenyRejoin = useCallback(async (gameIdToDeny, requesterId) => {
    try {
      const response = await axios.post(
        `${API}/multiplayer/rooms/${gameIdToDeny}/deny-rejoin/${requesterId}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      return response.data;
    } catch (error) {
      logger.error('❌ Failed to deny rejoin:', error);
      alert('Failed to deny rejoin request');
    }
  }, [authToken]);

  // Handler for receiving full game state (spectators get this on connect)
  const handleGameStateReceived = useCallback((data) => {
    // If refetch_needed is set, we need to fetch fresh game state from server
    // This happens after turn_skipped to ensure legal_moves are recalculated
    if (data.refetch_needed) {
      // Use ref to get the latest fetchGameState function
      if (fetchGameStateRef.current) {
        fetchGameStateRef.current();
      }
      return;
    }
    
    // Apply piece skins from host
    if (data.piece_skin_white !== undefined) {
      setPieceSkinWhite(data.piece_skin_white);
      window.chessKingdomPieceSkinWhite = data.piece_skin_white;
    }
    if (data.piece_skin_black !== undefined) {
      setPieceSkinBlack(data.piece_skin_black);
      window.chessKingdomPieceSkinBlack = data.piece_skin_black;
    }
    
    // Apply duke skins from host
    if (data.duke_skin_white) {
      setDukeSkinWhite(data.duke_skin_white);
    }
    if (data.duke_skin_black) {
      setDukeSkinBlack(data.duke_skin_black);
    }
    
    // Apply board theme via CSS variables
    const boardTheme = data.board_theme || (data.host_settings && data.host_settings.board_theme);
    if (boardTheme && boardTheme !== 'default') {
      const BOARD_THEMES = [
        { id: 'classic', light: '#f0d9b5', dark: '#b58863' },
        { id: 'blue', light: '#dee3e6', dark: '#8ca2ad' },
        { id: 'green', light: '#ffffdd', dark: '#86a666' },
        { id: 'purple', light: '#e8d4f2', dark: '#9b59b6' },
        { id: 'brown', light: '#f4e4c1', dark: '#a67c52' },
        { id: 'gray', light: '#e6e6e6', dark: '#888888' },
        { id: 'gold', light: '#f5e6c8', dark: '#c9a227' },
        { id: 'obsidian', light: '#c0c0c0', dark: '#303030' },
        { id: 'pink', light: '#ffe4ec', dark: '#d4849a' },
      ];
      const theme = BOARD_THEMES.find(t => t.id === boardTheme);
      if (theme) {
        const root = document.documentElement;
        root.style.setProperty('--board-light', theme.light);
        root.style.setProperty('--board-dark', theme.dark);
      }
    }
    
    // Apply piece colors for spectators
    const hostSettings = data.host_settings || {};
    if (hostSettings.use_custom_pieces && hostSettings.piece_color_white && hostSettings.piece_color_black) {
      const root = document.documentElement;
      root.style.setProperty('--piece-white', hostSettings.piece_color_white);
      root.style.setProperty('--piece-black', hostSettings.piece_color_black);
    } else if (hostSettings.piece_theme && hostSettings.piece_theme !== 'classic') {
      // Apply piece theme using official theme definitions
      const PIECE_THEMES = [
        { id: 'classic', white: '#ffffff', black: '#2d2d2d' },
        { id: 'red_blue', white: '#e74c3c', black: '#3498db' },
        { id: 'gold_silver', white: '#f1c40f', black: '#95a5a6' },
        { id: 'green_purple', white: '#2ecc71', black: '#9b59b6' },
        { id: 'orange_teal', white: '#e67e22', black: '#1abc9c' },
      ];
      const pieceTheme = PIECE_THEMES.find(t => t.id === hostSettings.piece_theme);
      if (pieceTheme) {
        const root = document.documentElement;
        root.style.setProperty('--piece-white', pieceTheme.white);
        root.style.setProperty('--piece-black', pieceTheme.black);
        root.setAttribute('data-piece-theme', hostSettings.piece_theme);
      } else {
        logger.warn(`🎨 Spectator: Unknown piece theme: ${hostSettings.piece_theme}`);
      }
    }
    
    // Apply board state if available
    if (data.board_state) {
      setGameState(data.board_state);
    }
  }, []);

  // Initialize WebSocket for multiplayer (includes spectator mode)
  const { connected, socketRef, sendMove, sendThemeChange, sendGameOver, sendRematchRequest, sendRematchDecline, spectatorCount } = useMultiplayerSocket(
    (gameMode === 'multiplayer' || gameMode === 'spectate') ? gameId : null,
    user?.user_id,
    handleMoveReceived,
    handleThemeChanged,
    handleGameOverSocket,
    handlePlayerDisconnected,
    handleRematchUpdate,
    isSpectator,
    null, // onSpectatorCountChange - we'll use spectatorCount directly
    handleInGameSpectatorRequest,  // Handle spectator requests in-game
    handleInGameRejoinRequest,     // Handle rejoin requests in-game
    handleGameStateReceived        // Handle full game state for spectators
  );

  const createNewGame = async (settings) => {
    try {
      const response = await axios.post(`${API}/chess/new-game`, settings);
      const { game_id, board_state } = response.data;
      setGameId(game_id);
      setGameState(board_state);
      setSelectedSquare(null);
      setLegalMoves([]);
      setInCheck(false);
      setIsCheckmate(false);
      setIsStalemate(false);
      setWinner(null);
      setShowNewGamePanel(false);
      setScreen('game');
      setVsAI(settings.vs_ai || false); // Track if playing against AI
    } catch (error) {
      logger.error('[CREATE GAME] Error creating new game:', error);
      logger.error('[CREATE GAME] Error details:', error.response?.data);
    }
  };

  const handleSquareClick = async (index) => {
    if (!gameId || !gameState || isCheckmate || isStalemate) {
      return;
    }

    // Spectators cannot interact with the board
    if (isSpectator) {
      return;
    }

    const clickedPiece = gameState.squares[index];
    // In multiplayer, check if player color is set and it's this player's turn
    if (gameMode === 'multiplayer') {
      // CRITICAL: If playerColor is not set yet, prevent interaction
      if (!playerColor) {
        return;
      }
      
      if (gameState.side_to_move !== playerColor) {
        return; // Not this player's turn
      }
    }

    // If no square selected, try to select this square
    if (selectedSquare === null) {
      // Helper: filter pre-fetched legal moves by "from" square (instant, no network)
      const filterLocal = (fromIdx) => allLegalMoves.filter(m => m.from === fromIdx);
      // In multiplayer, can only select your own pieces
      if (gameMode === 'multiplayer' && playerColor) {
        if (clickedPiece && clickedPiece.side === playerColor) {
          setSelectedSquare(index);
          setLegalMoves(filterLocal(index));
        }
      } else {
        // Solo/AI mode - can select pieces of side to move
        if (clickedPiece && clickedPiece.side === gameState.side_to_move) {
          setSelectedSquare(index);
          setLegalMoves(filterLocal(index));
        }
      }
      return;
    }

    // If clicking the same square, deselect
    if (selectedSquare === index) {
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }

    // If clicking another piece of the same side, switch selection
    const canSelectPiece = gameMode === 'multiplayer' && playerColor
      ? (clickedPiece && clickedPiece.side === playerColor)
      : (clickedPiece && clickedPiece.side === gameState.side_to_move);
      
    if (canSelectPiece) {
      setSelectedSquare(index);
      setLegalMoves(allLegalMoves.filter(m => m.from === index));
      return;
    }

    // Try to make a move
    const move = legalMoves?.find(m => m.to === index);
    if (move) {
      // Check if this is a promotion move
      const movingPiece = gameState.squares[selectedSquare];
      const toRank = Math.floor(index / 8);
      const isPromotion = movingPiece?.type === 'pawn' && (toRank === 7 || toRank === 0);
      if (isPromotion) {
        // Show promotion popup for pawn reaching 8th rank
        setPendingMove({ from: selectedSquare, to: index });
        setShowPromotionPopup(true);
      } else {
        // Make the move
        await makeMove(selectedSquare, index, null);
      }
    } else {
    }

    setSelectedSquare(null);
    setLegalMoves([]);
  };

  const makeMove = async (from, to, promotion, embodimentChoice = null) => {
    try {
      const payload = {
        from_pos: from,
        to_pos: to,
        promotion: promotion
      };
      
      if (embodimentChoice) {
        payload.embodiment_choice = embodimentChoice;
      }
      
      const response = await axios.post(`${API}/chess/game/${gameId}/move`, payload);
      const { 
        board_state, 
        legal_moves, 
        in_check, 
        is_checkmate, 
        is_stalemate, 
        winner: gameWinner,
        embodiment_condition_triggered,
        pending_embodiment_side,
        available_dukes
      } = response.data;
      
      // Record the move in history
      const movingPiece = gameState.squares[from];
      const capturedPiece = gameState.squares[to];
      
      const moveRecord = {
        piece: movingPiece,
        from: from,
        to: to,
        captured: capturedPiece,
        promotion: promotion,
        isCheck: in_check,
        isCheckmate: is_checkmate,
        isCastling: movingPiece?.type === 'king' && Math.abs(to - from) > 1
      };
      
      setMoveHistory(prev => [...prev, moveRecord]);
      setCurrentMoveIndex(prev => prev + 1);
      
      setGameState(board_state);
      setLegalMoves([]);
      setInCheck(in_check);
      setIsCheckmate(is_checkmate);
      setIsStalemate(is_stalemate);
      setWinner(gameWinner);
      
      // For multiplayer: broadcast move via WebSocket
      if (gameMode === 'multiplayer' && connected) {
        sendMove({ from_pos: from, to_pos: to, promotion }, board_state);
      }
      
      // Handle game over for multiplayer
      if (gameMode === 'multiplayer' && (is_checkmate || is_stalemate) && connected) {
        sendGameOver(gameWinner);
      }
      
      // Handle embodiment choice trigger for Two Dukes variant
      if (embodiment_condition_triggered && pending_embodiment_side && available_dukes) {
        setShowEmbodimentModal(true);
        setPendingEmbodimentSide(pending_embodiment_side);
        setAvailableDukes(available_dukes);
      }
    } catch (error) {
      logger.error('[MAKE MOVE] Error occurred:', error);
      logger.error('[MAKE MOVE] Error details:', error.response?.data);
      logger.error('[MAKE MOVE] Error status:', error.response?.status);
    }
  };

  const handlePromotionChoice = (pieceType) => {
    if (pendingMove) {
      makeMove(pendingMove.from, pendingMove.to, pieceType);
      setPendingMove(null);
    }
    setShowPromotionPopup(false);
  };

  const handleEmbodimentChoice = async (chosenDukePos) => {
    setShowEmbodimentModal(false);
    setPendingEmbodimentSide(null);
    setAvailableDukes([]);
    
    // Send embodiment choice to backend
    try {
      const response = await axios.post(`${API}/chess/game/${gameId}/move`, {
        from_pos: 0,  // Dummy value - backend will ignore when embodiment_choice is present
        to_pos: 0,    // Dummy value - backend will ignore when embodiment_choice is present
        embodiment_choice: chosenDukePos  // Just the position of chosen Duke
      });
      const { 
        board_state, 
        legal_moves, 
        in_check, 
        is_checkmate, 
        is_stalemate, 
        winner: gameWinner 
      } = response.data;
      
      setGameState(board_state);
      setLegalMoves([]);
      setInCheck(in_check);
      setIsCheckmate(is_checkmate);
      setIsStalemate(is_stalemate);
      setWinner(gameWinner);
    } catch (error) {
      logger.error('[EMBODIMENT CHOICE] Error occurred:', error);
      logger.error('[EMBODIMENT CHOICE] Error details:', error.response?.data);
    }
  };

  const handleSoloPlay = () => {
    if (!isAuthenticated) {
      setPendingAction('solo');
      setShowAuthModal(true);
    } else {
      setScreen('rules');
    }
  };

  const handleMultiplayerStart = () => {
    if (!isAuthenticated) {
      setPendingAction('multiplayer');
      setShowAuthModal(true);
    } else {
      setShowMultiplayerLobby(true);
    }
  };

  const handleAuthSuccess = () => {
    // Execute pending action after successful authentication
    if (pendingAction === 'solo') {
      setScreen('rules');
    } else if (pendingAction === 'multiplayer') {
      setShowMultiplayerLobby(true);
    } else {
      // Default: After login from ENTER button, go to courtyard lobby
      setScreen('lobby');
    }
    setPendingAction(null);
  };

  const handleGameStart = async (newGameId, mode, roomName) => {
    setGameMode(mode);
    setGameId(newGameId);
    setShowMultiplayerLobby(false);
    
    // Set spectator mode if joining as spectator
    const spectating = mode === 'spectate';
    setIsSpectator(spectating);
    if (spectating) {
      // For spectators, skip waiting room and go directly to game
      try {
        const response = await axios.get(`${API}/multiplayer/rooms/${newGameId}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        setGameState(response.data.board_state);
        setScreen('game');
      } catch (error) {
        logger.error('❌ Failed to fetch game state:', error);
        alert('Failed to load game for spectating. Please try again.');
        setScreen('lobby');
      }
      return;
    }
    
    // For players, go to waiting room
    const roomInfo = {
      gameId: newGameId,
      roomName: roomName || 'Game Room'
    };
    setWaitingRoomInfo(roomInfo);
    setShowWaitingRoom(true);
  };
  
  // Keep ref updated with handleGameStart
  useEffect(() => {
    handleGameStartRef.current = handleGameStart;
  });
  
  // Keyboard shortcut handler for DND toggle (press 'D' during game)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger in game view, for multiplayer, not spectators, and not typing in input
      if (screen === 'game' && 
          gameMode === 'multiplayer' && 
          !isSpectator && 
          e.key.toLowerCase() === 'd' &&
          !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        setDoNotDisturb(prev => !prev);
        triggerHapticFeedback('light');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [screen, gameMode, isSpectator]);

  // Listen for Pro Mode onboarding "Learn more" → open Rules screen
  useEffect(() => {
    const openRules = () => setScreen('rules');
    window.addEventListener('chessKingdomOpenRules', openRules);
    return () => window.removeEventListener('chessKingdomOpenRules', openRules);
  }, []);

  // Diagnostics page — opened via ?diagnostics=1 in URL, keyboard shortcut
  // Ctrl+Shift+D, or a global custom event. Available from ANY screen.
  const [showDiagnostics, setShowDiagnostics] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).has('diagnostics');
    } catch { return false; }
  });
  useEffect(() => {
    const open = () => setShowDiagnostics(true);
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd') && !['INPUT','TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        setShowDiagnostics(true);
      }
    };
    window.addEventListener('chessKingdomOpenDiagnostics', open);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('chessKingdomOpenDiagnostics', open);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Pre-fetch ALL legal moves whenever the board or side-to-move changes so
  // piece clicks can highlight instantly without a network roundtrip.
  const sideToMove = gameState?.side_to_move;
  const fullmoveNumber = gameState?.fullmove_number;
  useEffect(() => {
    if (!gameId || !gameState || isCheckmate || isStalemate) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/chess/game/${gameId}/legal-moves`);
        if (cancelled) return;
        setAllLegalMoves(response.data.legal_moves || []);
        allLegalMovesGameIdRef.current = gameId;
      } catch (err) {
        if (!cancelled) logger.error('[PREFETCH LEGAL MOVES] failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [gameId, sideToMove, fullmoveNumber, isCheckmate, isStalemate, gameState]);
  
  // Haptic feedback and push notification when it becomes the player's turn
  const prevSideToMoveRef = useRef(null);
  useEffect(() => {
    if (gameState && playerColor && screen === 'game' && gameMode === 'multiplayer' && !isSpectator) {
      const currentSide = gameState.side_to_move;
      const isMyTurn = currentSide === playerColor;
      const wasMyTurn = prevSideToMoveRef.current === playerColor;
      
      // If it just became my turn (and wasn't my turn before)
      if (isMyTurn && !wasMyTurn && prevSideToMoveRef.current !== null) {
        triggerHapticFeedback('turn');
        // Send push notification
        notifyYourTurn(opponentInfo?.name || 'Opponent');
      }
      
      prevSideToMoveRef.current = currentSide;
    }
  }, [gameState?.side_to_move, playerColor, screen, gameMode, isSpectator, notifyYourTurn, opponentInfo]);

  const handleGameReady = async () => {
    const currentGameId = waitingRoomInfo?.gameId || gameId;
    setShowWaitingRoom(false);
    
    // Fetch initial game state AND player color
    try {
      const response = await axios.get(`${API}/multiplayer/rooms/${currentGameId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const roomData = response.data;
      // Set player color IMMEDIATELY before setting screen to game
      // This prevents the race condition where playerColor is null when user tries to move
      let myColor = null;
      let opponentId = null;
      
      if (user && roomData.white_player_id === user.user_id) {
        myColor = 'white';
        opponentId = roomData.black_player_id;
      } else if (user && roomData.black_player_id === user.user_id) {
        myColor = 'black';
        opponentId = roomData.white_player_id;
      } else {
      }
      
      setPlayerColor(myColor);
      
      // Also fetch opponent info if we have an opponent
      if (opponentId) {
        try {
          const opponentResponse = await axios.get(`${API}/users/${opponentId}/profile`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          setOpponentInfo({
            user_id: opponentId,
            name: opponentResponse.data.name || opponentResponse.data.username || 'Opponent',
            avatar: opponentResponse.data.avatar || '👤',
            isPaid: opponentResponse.data.isPaid || false,
            rank: opponentResponse.data.rank || null,
            wins: opponentResponse.data.wins || 0,
            gamesPlayed: opponentResponse.data.gamesPlayed || 0
          });
        } catch (err) {
          setOpponentInfo({
            user_id: opponentId,
            name: 'Opponent',
            avatar: '👤',
            isPaid: false,
            rank: null
          });
        }
      }
      
      setGameState(roomData.board_state);
      setScreen('game');
    } catch (error) {
      logger.error('❌ Failed to fetch game state:', error);
      logger.error('❌ Error details:', error.response?.data || error.message);
      alert('Failed to load game. Please try again.');
      setScreen('lobby');
    }
  };

  const handleLeaveWaitingRoom = () => {
    setShowWaitingRoom(false);
    setWaitingRoomInfo(null);
    setGameId(null);
    setScreen('lobby');
  };

  // Reset all game state and return to lobby
  const handleLeaveGame = async () => {
    try {
      // Spectators can leave without any confirmation or API call
      if (isSpectator) {
        resetGameState();
        return;
      }
      
      // If in multiplayer mode, notify the server
      if (gameMode === 'multiplayer' && gameId) {
        const confirmLeave = window.confirm(
          'Are you sure you want to leave? This will forfeit the game and your opponent will win.'
        );
        
        if (!confirmLeave) return;
        
        try {
          const response = await axios.post(
            `${API}/multiplayer/rooms/${gameId}/leave`,
            {},
            { headers: { 'Authorization': `Bearer ${authToken}` } }
          );
          if (response.data.forfeited) {
            // Show the game over modal with forfeit result
            setGameOverData({ 
              winner: response.data.winner, 
              reason: 'forfeit' 
            });
            setShowGameOver(true);
            return; // Don't reset state yet - let the modal close handler do it
          }
        } catch (error) {
          logger.error('Error leaving game:', error);
        }
      }
      
      // Reset all game-related state (for non-multiplayer or if API failed)
      resetGameState();
      
    } catch (error) {
      logger.error('Error in handleLeaveGame:', error);
      resetGameState();
    }
  };
  
  // Helper function to reset all game state
  const resetGameState = () => {
    setGameState(null);
    setGameId(null);
    setGameMode(null);
    setLegalMoves([]);
    setSelectedSquare(null);
    setInCheck(false);
    setIsCheckmate(false);
    setIsStalemate(false);
    setWinner(null);
    setPlayerColor(null);
    setIsSpectator(false);
    setOpponentInfo(null);
    setWhitePlayerInfo(null);
    setBlackPlayerInfo(null);
    setVsAI(false);
    setMoveHistory([]);
    setCurrentMoveIndex(0);
    setShowGameOver(false);
    setGameOverData({ winner: null, reason: null });
    setRematchState(null);
    // Also reset waiting room state to prevent stale data
    setShowWaitingRoom(false);
    setWaitingRoomInfo(null);
    setShowMultiplayerLobby(false);
    setScreen('lobby');
  };

  // Handle closing game over modal
  const handleCloseGameOver = async () => {
    // Ensure backend knows the game is complete (backup call in case WebSocket didn't work)
    if (gameMode === 'multiplayer' && gameId) {
      try {
        await axios.post(`${API}/multiplayer/games/${gameId}/complete`, {
          winner: gameOverData.winner,
          reason: gameOverData.reason || 'game_over'
        }, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
      } catch (error) {
        // Game might already be completed, which is fine
      }
    }
    
    // Reset all game state using the shared function
    closeGameAndReturnToLobby();
  };

  // Handle rematch request
  const handleRematchRequest = () => {
    setRematchState('waiting');
    sendRematchRequest();
  };

  // Handle rematch decline
  const handleRematchDecline = () => {
    sendRematchDecline();
    // Don't close immediately, wait for the WebSocket to confirm
  };

  const handleGameEnd = useCallback(() => {
    if (isGuest && gameMode === 'multiplayer') {
      setShowGuestPrompt(true);
    }
  }, [isGuest, gameMode]);

  // Call handleGameEnd when game ends
  useEffect(() => {
    if (isCheckmate || isStalemate) {
      handleGameEnd();
    }
  }, [isCheckmate, isStalemate, handleGameEnd]);

  // Memoized player info to avoid inline object recreation on every render
  // IMPORTANT: These useMemo hooks must be called before any conditional returns
  const whitePlayerMemo = useMemo(() => {
    const getInfo = (field, fallback) => {
      if (isSpectator) return whitePlayerInfo?.[field] || fallback;
      if (vsAI) return user?.[field] || fallback;
      return playerColor === 'white' ? (user?.[field] || fallback) : (opponentInfo?.[field] || fallback);
    };
    return {
      name: isSpectator ? (whitePlayerInfo?.name || 'White Player')
        : vsAI ? (user?.name || user?.email?.split('@')[0] || 'You')
        : (playerColor === 'white' ? (user?.name || user?.email?.split('@')[0] || 'You') : (opponentInfo?.name || 'Opponent')),
      avatar: getInfo('avatar', '👤'), wins: getInfo('wins', 0),
      gamesPlayed: getInfo('gamesPlayed', 0), rating: getInfo('rank', null)
    };
  }, [isSpectator, vsAI, user, playerColor, opponentInfo, whitePlayerInfo]);

  const blackPlayerMemo = useMemo(() => {
    const getInfo = (field, fallback) => {
      if (isSpectator) return blackPlayerInfo?.[field] || fallback;
      if (vsAI) return fallback;
      return playerColor === 'black' ? (user?.[field] || fallback) : (opponentInfo?.[field] || fallback);
    };
    return {
      name: isSpectator ? (blackPlayerInfo?.name || 'Black Player')
        : vsAI ? 'AI Opponent'
        : (playerColor === 'black' ? (user?.name || user?.email?.split('@')[0] || 'You') : (opponentInfo?.name || 'Opponent')),
      avatar: isSpectator ? (blackPlayerInfo?.avatar || '👤') : vsAI ? '🤖' : getInfo('avatar', '👤'),
      wins: getInfo('wins', 0), gamesPlayed: getInfo('gamesPlayed', 0), rating: getInfo('rank', null)
    };
  }, [isSpectator, vsAI, user, playerColor, opponentInfo, blackPlayerInfo]);

  // Cover Screen
  if (screen === 'cover') {
    return (
      <div className="App">
        <CoverScreen 
          onStart={handleSoloPlay}
          onMultiplayer={handleMultiplayerStart}
          onLogin={() => setShowAuthModal(true)}
          onOpenAbout={() => setScreen('about')}
          onOpenLobbyChat={() => {
            if (!isAuthenticated) {
              setShowAuthModal(true);
            } else {
              setShowLobbyChat(true);
            }
          }}
          onOpenSettings={() => setShowSettings(true)}
          onOpenAssistant={() => setScreen('assistant')}
          onOpenTroubleshooter={() => setScreen('troubleshooter')}
          onOpenAccount={() => setScreen('account')}
        />
        
        {showAuthModal && (
          <AuthModal 
            isOpen={showAuthModal}
            onClose={() => {
              setShowAuthModal(false);
              setPendingAction(null);
            }}
            onSuccess={handleAuthSuccess}
          />
        )}
        
        {showMultiplayerLobby && (
          <MultiplayerLobby
            onClose={() => setShowMultiplayerLobby(false)}
            onGameStart={handleGameStart}
            globalNotifications={globalNotifications}
            addGlobalNotification={addGlobalNotification}
            clearGlobalNotification={clearGlobalNotification}
            clearAllGlobalNotifications={clearAllGlobalNotifications}
          />
        )}

        {showLobbyChat && (
          <LobbyChat
            isOpen={showLobbyChat}
            onClose={() => setShowLobbyChat(false)}
          />
        )}

        {/* Waiting Room */}
        {showWaitingRoom && waitingRoomInfo && (
          <WaitingRoom
            gameId={waitingRoomInfo.gameId}
            roomName={waitingRoomInfo.roomName}
            onGameReady={handleGameReady}
            onLeave={handleLeaveWaitingRoom}
            addGlobalNotification={addGlobalNotification}
          />
        )}

        {/* Settings Modal */}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />

        {/* Diagnostics Page — accessible via ?diagnostics=1 even when unauthenticated */}
        {showDiagnostics && <DiagnosticsPage onClose={() => setShowDiagnostics(false)} />}

        {/* PWA install prompt — auto-hides when already installed / dismissed */}
        <InstallPWAButton variant="floating" />
      </div>
    );
  }

  // About Us Page
  if (screen === 'about') {
    return (
      <div className="App full-page-screen">
        <AboutUs onBack={() => setScreen('cover')} />
      </div>
    );
  }

  // Courtyard Lobby - Post-Login Hub
  if (screen === 'lobby') {
    return (
      <div className="App full-page-screen">
        {/* Global Notification Bell - Top Left */}
        <div className="global-notification-bell">
          <NotificationBell
            notifications={globalNotifications}
            onDismiss={clearGlobalNotification}
            onClearAll={clearAllGlobalNotifications}
            onEnterRoom={handleEnterRoomFromNotification}
            onApproveSpectator={handleApproveSpectator}
            onDenySpectator={handleDenySpectator}
            onApproveRejoin={handleApproveRejoin}
            onDenyRejoin={handleDenyRejoin}
          />
        </div>
        
        <CourtyardLobby
          onNavigate={(destination) => {
            if (destination === 'rules') setScreen('rules');
            else if (destination === 'multiplayer') setShowMultiplayerLobby(true);
            else if (destination === 'tournaments') setShowTournamentLobby(true);
            else if (destination === 'ranked') setShowRankedMatchmaking(true);  // Ranked system
            else if (destination === 'chatroom') setShowLobbyChat(true);
            else if (destination === 'newgame') setShowNewGamePanel(true);
            else if (destination === 'profile') setScreen('profile');
            else if (destination === 'subscription') setScreen('subscription');
            else if (destination === 'quickplay') {
              // Quick Play: Fight to be King vs AI
              createNewGame({
                variant: 'fight_to_be_king',
                vs_ai: true,
                ai_difficulty: 1,
                white_embodiment: 'fight_to_be_king',
                black_embodiment: 'fight_to_be_king'
              });
            }
            else if (destination === 'cover') setScreen('cover');
          }}
          onOpenSettings={() => setShowSettings(true)}
          onOpenAssistant={() => setScreen('assistant')}
          onOpenTroubleshooter={() => setScreen('troubleshooter')}
          addGlobalNotification={addGlobalNotification}
        />
        
        {/* Modals that can open from lobby */}
        <ProModeOnboarding />
        <VersionMonitor isInGame={screen === 'game'} />
        {showDiagnostics && <DiagnosticsPage onClose={() => setShowDiagnostics(false)} />}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        
        {showMultiplayerLobby && (
          <MultiplayerLobby
            onClose={() => setShowMultiplayerLobby(false)}
            onGameStart={handleGameStart}
            globalNotifications={globalNotifications}
            addGlobalNotification={addGlobalNotification}
            clearGlobalNotification={clearGlobalNotification}
            clearAllGlobalNotifications={clearAllGlobalNotifications}
          />
        )}
        
        {showTournamentLobby && (
          <TournamentLobby
            user={user}
            token={authToken}
            onClose={() => setShowTournamentLobby(false)}
            onJoinGame={(tournament) => {
              setActiveTournamentId(tournament.tournament_id);
              setShowTournamentLobby(false);
              setShowTournamentBracket(true);
            }}
            addNotification={(type, msg) => addGlobalNotification(type, null, null, msg)}
          />
        )}
        
        {showTournamentBracket && activeTournamentId && (
          <TournamentBracket
            tournamentId={activeTournamentId}
            token={authToken}
            user={user}
            onClose={() => {
              setShowTournamentBracket(false);
              setActiveTournamentId(null);
            }}
            onJoinMatch={(gameId, isSpectator = false) => {
              setShowTournamentBracket(false);
              handleGameStart({
                gameId,
                playerColor: null, // Will be determined by backend
                variant: 'instant_king',
                isSpectator
              });
            }}
            addNotification={(type, msg) => addGlobalNotification(type, null, null, msg)}
          />
        )}
        
        {/* Ranked Matchmaking Modal */}
        {showRankedMatchmaking && (
          <RankedMatchmaking
            user={user}
            token={authToken}
            onClose={() => setShowRankedMatchmaking(false)}
            onJoinGame={(gameId) => {
              setShowRankedMatchmaking(false);
              handleGameStart({
                gameId,
                playerColor: null,
                variant: 'instant_king',
                isSpectator: false
              });
            }}
            addNotification={(type, msg) => addGlobalNotification(type, null, null, msg)}
          />
        )}
        
        {showLobbyChat && (
          <LobbyChat
            isOpen={showLobbyChat}
            onClose={() => setShowLobbyChat(false)}
          />
        )}
        
        {showNewGamePanel && (
          <NewGamePanel
            onNewGame={createNewGame}
            onClose={() => setShowNewGamePanel(false)}
          />
        )}
        
        {/* Waiting Room */}
        {showWaitingRoom && waitingRoomInfo && (
          <WaitingRoom
            gameId={waitingRoomInfo.gameId}
            roomName={waitingRoomInfo.roomName}
            onGameReady={handleGameReady}
            onLeave={handleLeaveWaitingRoom}
            addGlobalNotification={addGlobalNotification}
          />
        )}
      </div>
    );
  }

  // Profile Page
  if (screen === 'profile') {
    return (
      <div className="App full-page-screen">
        <ProfilePage 
          onBack={() => setScreen('lobby')}
          onUpgrade={() => setShowStore(true)}
          onNavigate={(destination) => setScreen(destination)}
        />
        
        {showStore && (
          <StoreModal
            isOpen={showStore}
            onClose={() => setShowStore(false)}
          />
        )}
      </div>
    );
  }

  // Subscription / Membership Page
  if (screen === 'subscription') {
    return (
      <div className="App full-page-screen">
        <SubscriptionPage
          onBack={() => setScreen('lobby')}
          onUpgrade={() => setShowStore(true)}
        />

        {showStore && (
          <StoreModal
            isOpen={showStore}
            onClose={() => setShowStore(false)}
          />
        )}
      </div>
    );
  }

  // Rules Screen
  if (screen === 'rules') {
    return (
      <div className="App">
        <RulesScreen 
          onContinue={() => setShowNewGamePanel(true) || setScreen('game')}
          onBack={() => setScreen(isAuthenticated ? 'lobby' : 'cover')}
          hasActiveGame={gameState !== null}
          onBackToGame={() => setScreen('game')}
          currentVariant={gameState?.variant}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  // Assistant Page
  if (screen === 'assistant') {
    return (
      <div className="App full-page-screen">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => setScreen('lobby')}>
            ← Back to Lobby
          </button>
          <h1>🤖 Chess Kingdom Assistant</h1>
          <button 
            className="close-page-btn"
            onClick={() => setScreen('lobby')}
            title="Close"
          >
            ✕
          </button>
        </div>
        <Assistant isOpen={true} onClose={() => setScreen('lobby')} isFullPage={true} />
      </div>
    );
  }

  // Troubleshooter Page
  if (screen === 'troubleshooter') {
    return (
      <div className="App full-page-screen">
        <div className="page-header">
          <button 
            className="btn btn-secondary" 
            onClick={() => setScreen('lobby')}
            style={{ zIndex: 1000, position: 'relative', pointerEvents: 'auto' }}
          >
            ← Back to Lobby
          </button>
          <h1>🔧 Troubleshooter</h1>
          <button 
            className="close-page-btn"
            onClick={() => setScreen('lobby')}
            title="Close"
          >
            ✕
          </button>
        </div>
        <Troubleshooter isOpen={true} onClose={() => setScreen('lobby')} isFullPage={true} />
      </div>
    );
  }

  // Account Management Page
  if (screen === 'account') {
    return (
      <div className="App full-page-screen">
        <div className="page-header">
          <button 
            className="btn btn-secondary" 
            onClick={() => setScreen('cover')}
            style={{ zIndex: 1000, position: 'relative', pointerEvents: 'auto' }}
          >
            ← Back to Home
          </button>
          <h1>👤 Account Management</h1>
          <button 
            className="close-page-btn"
            onClick={() => setScreen('cover')}
            title="Close"
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '30px', maxWidth: '800px', margin: '0 auto' }}>
          <AccountManager isOpen={true} onClose={() => setScreen('cover')} isFullPage={true} />
        </div>
      </div>
    );
  }

  // New Game Panel (before game starts)
  if (screen === 'game' && !gameState) {
    return (
      <div className="App">
        <NewGamePanel 
          onNewGame={createNewGame}
          onClose={() => setScreen('cover')}
          onOpenSettings={() => setShowSettings(true)}
        />
        
        {/* Settings Modal */}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </div>
    );
  }

  // Game Screen
  if (!gameState) {
    return (
      <div className="App">
        <div className="loading">Loading game...</div>
      </div>
    );
  }


  return (
    <div className="App">
      {/* Trial Banner - shows at top when trial is active */}
      {isAuthenticated && <TrialBanner />}

      {/* In-Game Notification Bell (for room owners to see spectator requests) */}
      {gameMode === 'multiplayer' && !isSpectator && (
        <div className="in-game-notification-bell" data-testid="in-game-notification-bell" style={{
          position: 'fixed',
          top: isTrialActive && isAuthenticated ? '70px' : '20px',
          left: '20px',
          zIndex: 1002
        }}>
          <NotificationBell
            notifications={globalNotifications}
            onDismiss={clearGlobalNotification}
            onClearAll={clearAllGlobalNotifications}
            onEnterRoom={handleEnterRoomFromNotification}
            doNotDisturb={doNotDisturb}
            onApproveSpectator={handleApproveSpectator}
            onDenySpectator={handleDenySpectator}
            onApproveRejoin={handleApproveRejoin}
            onDenyRejoin={handleDenyRejoin}
          />
        </div>
      )}

      <div className="game-container" style={{ marginTop: isTrialActive && isAuthenticated ? '30px' : '0' }}>
        <VersionMonitor isInGame={screen === 'game'} />
        {showDiagnostics && <DiagnosticsPage onClose={() => setShowDiagnostics(false)} />}
        <header className="game-header">
          <div className="variant-banner">
            {getVariantDisplay(gameState?.variant || 'kings_court_2dukes')}
          </div>
          
          {/* Inline Turn Indicator - shows between board name and variant */}
          {/* Loading indicator when playerColor is not yet set */}
          {gameMode === 'multiplayer' && !playerColor && !isSpectator && (
            <div 
              className="inline-turn-indicator loading-color" 
              data-testid="player-color-loading" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '16px',
                fontWeight: 'bold',
                fontSize: '12px',
                gap: '6px',
                marginLeft: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
              <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
              <span>Loading game...</span>
            </div>
          )}

          {gameMode === 'multiplayer' && playerColor && !isSpectator && (
            <div 
              className="inline-turn-indicator" 
              data-testid="player-turn-indicator" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: gameState?.side_to_move === playerColor 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '16px',
                fontWeight: 'bold',
                fontSize: '12px',
                gap: '6px',
                marginLeft: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
              <span style={{ fontSize: '12px' }}>
                {playerColor === 'white' ? '⚪' : '⚫'}
              </span>
              <span style={{ 
                animation: gameState?.side_to_move === playerColor ? 'pulse 1.5s infinite' : 'none'
              }}>
                {gameState?.side_to_move === playerColor 
                  ? '🟢 YOUR TURN' 
                  : "⏳ Opponent's Turn"}
              </span>
              {spectatorCount > 0 && (
                <span style={{
                  background: 'rgba(255,255,255,0.2)',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '10px',
                  marginLeft: '4px'
                }}>
                  👁️ {spectatorCount}
                </span>
              )}
            </div>
          )}

          {/* Spectator Mode Indicator */}
          {isSpectator && (
            <div 
              className="inline-turn-indicator spectator-mode"
              data-testid="spectator-indicator" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '16px',
                fontWeight: 'bold',
                fontSize: '12px',
                marginLeft: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                gap: '8px'
              }}>
              <span>👁️ SPECTATOR</span>
              <span style={{ fontSize: '11px', opacity: 0.9 }}>
                {gameState?.side_to_move === 'white' ? '⚪ White to move' : '⚫ Black to move'}
              </span>
            </div>
          )}
          
          {/* User Profiles - Show players */}
          <div className="player-profiles">
            <UserProfile 
              user={user} 
              isCurrentPlayer={gameState?.current_side === 'white'} 
              isPaid={hasActiveAccess()}
              isAI={vsAI && gameState?.current_side === 'black'}
            />
            {gameMode === 'multiplayer' && opponentInfo && (
              <UserProfile 
                user={opponentInfo} 
                isCurrentPlayer={gameState?.current_side === 'black'} 
                isPaid={opponentInfo.isPaid}
                isAI={false}
                isOpponent={true}
              />
            )}
            {vsAI && (
              <UserProfile 
                isAI={true}
              />
            )}
          </div>

          <div className="header-actions">
            <ThemeToggle />
            <button 
              className={`btn btn-icon flip-board-btn ${boardFlipped ? 'active' : ''}`}
              onClick={() => setBoardFlipped(!boardFlipped)}
              title={boardFlipped ? "View from White's side" : "View from Black's side"}
              data-testid="flip-board-btn"
              style={boardFlipped ? { backgroundColor: '#10b981' } : {}}
            >
              🔄
            </button>
            <button 
              className="btn btn-icon" 
              onClick={() => setShowFriends(true)}
              title="Friends (Premium)"
              data-testid="friends-panel-btn"
            >
              👥
            </button>
            {gameMode === 'multiplayer' && (
              <button 
                className="btn btn-danger" 
                onClick={async () => {
                  if (window.confirm('Are you sure you want to resign? This will end the game.')) {
                    try {
                      // Call resign API
                      const response = await axios.post(`${API}/chess/game/${gameId}/resign`, {}, {
                        headers: { 'Authorization': `Bearer ${authToken}` }
                      });
                      const { winner, reason } = response.data;
                      
                      // Show game over modal
                      setGameOverData({ winner, reason });
                      setShowGameOver(true);
                    } catch (error) {
                      logger.error('Error resigning:', error);
                      // Fallback: just end the game
                      setGameOverData({ winner: null, reason: 'resignation' });
                      setShowGameOver(true);
                    }
                  }
                }}
                data-testid="resign-btn"
                title="Resign Game"
              >
                🏳️ Resign
              </button>
            )}
            <button 
              className="btn btn-secondary" 
              onClick={handleLeaveGame}
              title="Leave Game"
            >
              🚪 Leave
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => setScreen('rules')}
              data-testid="view-rules-btn"
            >
              Rules
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowNewGamePanel(true)}
              data-testid="new-game-btn"
            >
              New Game
            </button>
          </div>
        </header>

        <GameStatus
          gameState={gameState}
          inCheck={inCheck}
          isCheckmate={isCheckmate}
          isStalemate={isStalemate}
          winner={winner}
        />

        {/* Host Theme Indicator - shows when playing with another player's theme in multiplayer */}
        {hostThemeInfo && hostThemeInfo.isUsingHostTheme && (gameMode === 'multiplayer' || gameMode === 'spectate') && (
          <div 
            className="host-theme-indicator"
            data-testid="host-theme-indicator"
            style={{
              textAlign: 'center',
              padding: '6px 12px',
              backgroundColor: 'rgba(201, 162, 39, 0.15)',
              border: '1px solid rgba(201, 162, 39, 0.4)',
              borderRadius: '6px',
              marginBottom: '8px',
              fontSize: '0.85rem',
              color: '#c9a227',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '1rem' }}>🎨</span>
            <span>Playing with <strong>{hostThemeInfo.hostName}&apos;s</strong> theme</span>
          </div>
        )}

        {/* Move Timer - shows countdown for timed games (visible to players and spectators) */}
        {(gameMode === 'multiplayer' || gameMode === 'spectate') && moveTimerLimit && gameState && (
          <div className="timer-container">
            <MoveTimer
              timeLimit={moveTimerLimit}
              isActive={screen === 'game' && !isCheckmate && !isStalemate}
              playerColor={playerColor}
              currentTurn={gameState?.side_to_move}
              gameId={gameId}
              isSpectator={isSpectator}
              onTimeOut={() => {
                // MoveTimer already verified this is our turn, just send the skip
                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                  socketRef.current.send(JSON.stringify({
                    type: 'skip_turn',
                    reason: 'timeout',
                    player: playerColor
                  }));
                } else {
                  logger.error('❌ WebSocket not connected, cannot send skip_turn');
                }
              }}
            />
          </div>
        )}

        {/* Player Info Panels - Shows player names, avatars, and colors above the board */}
        {gameState && (gameMode === 'multiplayer' || gameMode === 'spectate' || vsAI) && (
          <PlayerInfoPanel
            whitePlayer={whitePlayerMemo}
            blackPlayer={blackPlayerMemo}
            currentTurn={gameState?.side_to_move}
            isSpectator={isSpectator}
            playerColor={playerColor}
            moveNumber={moveHistory.length}
            showStats={gameMode === 'multiplayer' || gameMode === 'spectate'}
            spectatorCount={spectatorCount}
          />
        )}

        {/* Removed Full 3D Models view mode - was causing issues with piece movement
        {viewMode === '3dmodels' ? (
          <ChessBoard3D
            boardState={gameState}
            onSquareClick={handleSquareClick}
            selectedSquare={selectedSquare}
            legalMoves={legalMoves}
            theme3D={theme3D}
          />
        ) : ( */}
          <ChessBoard
            gameState={gameState}
            onSquareClick={handleSquareClick}
            selectedSquare={selectedSquare}
            legalMoves={legalMoves}
            inCheck={inCheck}
            pieceSkinWhite={pieceSkinWhite}
            pieceSkinBlack={pieceSkinBlack}
            dukeSkinWhite={dukeSkinWhite}
            dukeSkinBlack={dukeSkinBlack}
            boardFlipped={boardFlipped}
          />
        {/* )} */}

        {/* Move History Sidebar */}
        <MoveHistory 
          moves={moveHistory}
          currentMoveIndex={currentMoveIndex}
        />

        {/* In-Game Chat (Multiplayer Only - Players) */}
        {gameMode === 'multiplayer' && !isSpectator && (
          <ChatBox 
            socketRef={socketRef}
            gameId={gameId}
          />
        )}

        {/* Spectator Chat (Spectators Only) */}
        {isSpectator && gameId && (
          <SpectatorChat gameId={gameId} />
        )}

        {showPromotionPopup && (
          <PromotionPopup
            side={gameState.side_to_move}
            variant={gameState.variant}
            onChoose={handlePromotionChoice}
            onClose={() => {
              setShowPromotionPopup(false);
              setPendingMove(null);
            }}
          />
        )}

        {showEmbodimentModal && (
          <EmbodimentChoiceModal
            isOpen={showEmbodimentModal}
            title={`Choose Duke to Ascend - ${pendingEmbodimentSide === 'white' ? 'White' : 'Black'}`}
            side={pendingEmbodimentSide}
            availableDukes={availableDukes}
            onChoose={handleEmbodimentChoice}
            onClose={() => {
              setShowEmbodimentModal(false);
              setPendingEmbodimentSide(null);
              setAvailableDukes([]);
            }}
          />
        )}

        {showNewGamePanel && (
          <NewGamePanel
            onNewGame={createNewGame}
            onClose={() => setShowNewGamePanel(false)}
          />
        )}

        {/* Floating Settings Button */}
        <button 
          className="floating-settings-btn" 
          onClick={() => setShowSettings(true)}
          title="Settings - Customize board and piece colors"
        >
          ⚙️
        </button>

        {/* Floating Assistant Button */}
        <button 
          className="floating-assistant-btn" 
          onClick={() => setShowAssistant(true)}
          title="Assistant - Get help and tips"
        >
          🤖
        </button>

        {/* Floating Troubleshooter Button */}
        <button 
          className="floating-troubleshooter-btn" 
          onClick={() => setShowTroubleshooter(true)}
          title="Troubleshooter - Run diagnostics"
        >
          🔧
        </button>

        {/* Settings Modal */}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          isMultiplayer={gameMode === 'multiplayer' || gameMode === 'spectate'}
        />

        {/* Game Over Modal */}
        <GameOverModal
          isOpen={showGameOver}
          winner={gameOverData.winner}
          reason={gameOverData.reason}
          playerColor={playerColor}
          isMultiplayer={gameMode === 'multiplayer'}
          rematchState={rematchState}
          opponentName={opponentInfo?.name || 'Opponent'}
          onRematchRequest={handleRematchRequest}
          onRematchDecline={handleRematchDecline}
          onClose={handleCloseGameOver}
        />

        {/* Assistant Modal */}
        <Assistant
          isOpen={showAssistant}
          onClose={() => setShowAssistant(false)}
          isFullPage={false}
        />

        {/* Troubleshooter Modal */}
        <Troubleshooter
          isOpen={showTroubleshooter}
          onClose={() => setShowTroubleshooter(false)}
          isFullPage={false}
        />

        {/* Friends System Modal */}
        <FriendsSystem
          isOpen={showFriends}
          onClose={() => setShowFriends(false)}
        />

        {/* Avatar Selector Modal */}
        <AvatarSelector
          isOpen={showAvatarSelector}
          onClose={() => setShowAvatarSelector(false)}
          onSelect={() => {
          }}
          currentAvatar={user?.avatar || '👤'}
        />

        {/* Guest Registration Prompt */}
        {showGuestPrompt && (
          <GuestPrompt
            isOpen={showGuestPrompt}
            onRegister={() => {
              setShowGuestPrompt(false);
              setShowAuthModal(true);
            }}
            onDismiss={() => setShowGuestPrompt(false)}
          />
        )}

        {/* Store Modal */}
        {showStore && (
          <StoreModal
            isOpen={showStore}
            onClose={() => setShowStore(false)}
          />
        )}
      </div>
    </div>
  );
}

export default App;
